import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PhoneIphone from '@mui/icons-material/PhoneIphone';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import EditOutlined from '@mui/icons-material/EditOutlined';
import type { ApparecchioAr, ParteAr, SegnoAr } from '../types/ApparecchiArTypes';
import {
  conteggio,
  elencaApparecchi,
  pubblica,
  risolviImmagini,
  salvaAnagrafica,
  salvaSegni,
} from '../services/apparecchiArService';
import TavoloDiMappatura, {
  COLORI,
  RAGGIO_PREDEFINITO,
  coloreDi,
} from '../components/ar/TavoloDiMappatura';
import ConfrontoModelli from '../components/ar/ConfrontoModelli';
import SchedaParte from '../components/ar/SchedaParte';
import Riferimenti from '../components/ar/Riferimenti';

/**
 * Il banco di rifinitura degli apparecchi in realtà aumentata.
 *
 * ⚠️ **Qui non si creano apparecchi, si rifiniscono.** Le fotografie si fanno dove sta
 * l'apparecchio, con YumaWORK in mano: serve la sua luce, le sue targhette, e ARCore
 * che giudica lo scatto sul posto. Nessuna schermata web può farlo. Quello che qui si fa
 * meglio è mettere i punti al pixel giusto, con il mouse e uno schermo grande.
 *
 * ⚠️ **Il colore di un punto dice da dove viene, non che aspetto ha.** Verde = l'ha
 * messo una persona; azzurro = viene dalla scritta serigrafata letta dal telefono; viola
 * tratteggiato = è una proposta di un modello che nessuno ha ancora guardato. Su un
 * apparecchio in corrente continua quella distinzione è la cosa più importante della
 * schermata.
 */
export default function ApparecchiArPage() {
  const [apparecchi, setApparecchi] = useState<ApparecchioAr[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [apertoId, setApertoId] = useState<string | null>(null);
  const [aperto, setAperto] = useState<ApparecchioAr | null>(null);
  const [superficieId, setSuperficieId] = useState<string | null>(null);
  const [parteScelta, setParteScelta] = useState<string | null>(null);
  const [inSalvataggio, setInSalvataggio] = useState(false);
  /*
    ⚠️ L'anteprima delle proposte NON entra nei dati: vive solo in questo stato finché
    qualcuno non la accetta. Scriverla subito su Firestore renderebbe indistinguibile
    «l'ho guardata» da «l'ha detta un modello» nel momento esatto in cui quella
    distinzione serve.
  */
  const [anteprima, setAnteprima] = useState<SegnoAr[] | null>(null);
  const [schedaAperta, setSchedaAperta] = useState(false);
  const [parteInModifica, setParteInModifica] = useState<ParteAr | null>(null);
  /*
    ⚠️ **Armato è uno stato separato dalla selezione, di proposito.** Selezionare una
    parte serve a guardarla, a correggerne il nome, a cambiarne la taglia del cerchio;
    armare serve a scrivere una coordinata. Confonderli è ciò che faceva spostare un
    punto a ogni clic sulla fotografia — e uno spostamento accidentale non lascia traccia
    di essere accidentale.
  */
  const [armato, setArmato] = useState(false);

  // ⚠️ Esc disarma sempre. È la via d'uscita che chi sbaglia modalità cerca per primo.
  useEffect(() => {
    const suTasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setArmato(false);
    };
    window.addEventListener('keydown', suTasto);
    return () => window.removeEventListener('keydown', suTasto);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setApparecchi(await elencaApparecchi());
      } catch (e) {
        // ⚠️ «Non ce ne sono» e «non lo so» sono due cose diverse: mostrarle insieme
        // significa dichiarare vuoto un catalogo proprio quando non si è riusciti a
        // guardarci dentro.
        setErrore(e instanceof Error ? e.message : 'la lettura del catalogo non è riuscita');
      } finally {
        setCaricamento(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!apertoId) {
      setAperto(null);
      return;
    }
    const grezzo = apparecchi.find((a) => a.id === apertoId);
    if (!grezzo) return;
    void (async () => {
      const risolto = await risolviImmagini(grezzo);
      setAperto(risolto);
      setSuperficieId(risolto.superfici[0]?.id ?? null);
      setParteScelta(risolto.parti[0]?.id ?? null);
      setArmato(false);
    })();
  }, [apertoId, apparecchi]);

  /*
    ⚠️ **Si precaricano le altre facce appena l'apparecchio è aperto.** Sono quattro
    fotografie da otto megabyte: senza, ogni cambio di scheda fa aspettare da capo. Così
    aspetta solo la prima, e le altre sono già nella cache del browser quando servono.
    Non blocca niente — se il precaricamento non finisce in tempo, si vede comunque
    l'attesa dichiarata.
  */
  useEffect(() => {
    if (!aperto) return;
    aperto.superfici.forEach((s) => {
      if (!s.immagine_url) return;
      const img = new Image();
      img.src = s.immagine_url;
    });
  }, [aperto]);

  const superficie = useMemo(
    () => aperto?.superfici.find((s) => s.id === superficieId) ?? null,
    [aperto, superficieId],
  );

  const scriviSegni = useCallback(
    async (segni: SegnoAr[]) => {
      if (!aperto || !superficie) return;
      const nuove = aperto.superfici.map((s) =>
        s.id === superficie.id ? { ...s, segni } : s,
      );
      setAperto({ ...aperto, superfici: nuove });
      setInSalvataggio(true);
      try {
        await salvaSegni(aperto.id, superficie, segni, nuove);
      } catch (e) {
        setErrore(e instanceof Error ? e.message : 'il salvataggio non è riuscito');
      } finally {
        setInSalvataggio(false);
      }
    },
    [aperto, superficie],
  );

  /**
   * Posa o sposta il punto della parte scelta.
   *
   * ⚠️ Una parte sta in un posto solo per faccia: toccando di nuovo si SPOSTA, non si
   * aggiunge. Due richiami con la stessa sigla sulla stessa fotografia sarebbero una
   * contraddizione visibile, e chi la vede non ha modo di sapere a quale credere.
   */
  const poni = (x: number, y: number) => {
    if (!superficie || !parteScelta) return;
    const raggio = Math.round(superficie.larghezza_pixel * RAGGIO_PREDEFINITO);
    void scriviSegni([
      ...superficie.segni.filter((g) => g.parte !== parteScelta),
      { parte: parteScelta, x, y, r: raggio, origine: 'umano' },
    ]);
  };

  const sposta = (parte: string, x: number, y: number) => {
    if (!superficie) return;
    void scriviSegni(
      superficie.segni.map((g) =>
        // ⚠️ Spostare a mano una bozza la PROMUOVE a punto umano: qualcuno l'ha
        // guardata e corretta, ed è esattamente la conferma che mancava.
        g.parte === parte ? { ...g, x, y, origine: 'umano' } : g,
      ),
    );
  };

  /** Cambia la taglia del cerchio di una parte. La posizione non si tocca. */
  const raggio = (parte: string, r: number) => {
    if (!superficie) return;
    void scriviSegni(superficie.segni.map((g) => (g.parte === parte ? { ...g, r } : g)));
  };

  /** Crea o corregge una parte del catalogo di questo apparecchio. */
  const salvaParte = async (p: ParteAr) => {
    if (!aperto) return;
    const esiste = aperto.parti.some((x) => x.id === p.id);
    const parti = esiste ? aperto.parti.map((x) => (x.id === p.id ? p : x)) : [...aperto.parti, p];
    setAperto({ ...aperto, parti });
    setSchedaAperta(false);
    setParteScelta(p.id);
    try {
      await salvaAnagrafica(aperto.id, { parti });
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'il salvataggio della parte non è riuscito');
    }
  };

  /**
   * Toglie una parte dal catalogo.
   *
   * ⚠️ **Con lei se ne vanno i suoi segni su OGNI faccia.** Un cerchio che nomina una
   * parte inesistente non produce un errore: produce un'indicazione senza etichetta in
   * cantiere, che è peggio di nessuna indicazione.
   */
  const togliParte = async (id: string) => {
    if (!aperto) return;
    const parti = aperto.parti.filter((p) => p.id !== id);
    const superfici = aperto.superfici.map((s) => ({
      ...s,
      segni: s.segni.filter((g) => g.parte !== id),
    }));
    setAperto({ ...aperto, parti, superfici });
    if (parteScelta === id) setParteScelta(parti[0]?.id ?? null);
    try {
      await salvaAnagrafica(aperto.id, { parti });
      const conSegni = superfici.find((s) => s.id === superficieId);
      if (conSegni) await salvaSegni(aperto.id, conSegni, conSegni.segni, superfici);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'la rimozione non è riuscita');
    }
  };

  const togli = (parte: string) => {
    if (!superficie) return;
    void scriviSegni(superficie.segni.filter((g) => g.parte !== parte));
  };

  if (caricamento) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ── elenco ────────────────────────────────────────────────────────────────
  if (!aperto) {
    return (
      <Box>
        <Intestazione />
        {errore && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errore}
          </Alert>
        )}
        {apparecchi.length === 0 && !errore ? (
          <VuotoConDirezione />
        ) : (
          <Stack spacing={1.25}>
            {apparecchi.map((a) => (
              <VoceApparecchio key={a.id} a={a} onApri={() => setApertoId(a.id)} />
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  // ── banco ─────────────────────────────────────────────────────────────────
  const senzaSegni = aperto.superfici.every((s) => s.segni.length === 0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Button size="small" onClick={() => setApertoId(null)}>
          ← Tutti gli apparecchi
        </Button>
        <Typography sx={{ fontWeight: 600 }}>
          {aperto.marca} {aperto.modello}
        </Typography>
        <Chip
          size="small"
          label={aperto.pubblicato ? 'in circolazione' : 'non pubblicato'}
          color={aperto.pubblicato ? 'success' : 'default'}
          variant={aperto.pubblicato ? 'filled' : 'outlined'}
        />
        <Box sx={{ flex: 1 }} />
        {inSalvataggio && (
          <Typography variant="caption" color="text.secondary">
            salvo…
          </Typography>
        )}
        <Button
          variant={aperto.pubblicato ? 'outlined' : 'contained'}
          size="small"
          disabled={!aperto.pubblicato && senzaSegni}
          onClick={async () => {
            await pubblica(aperto.id, !aperto.pubblicato);
            setAperto({ ...aperto, pubblicato: !aperto.pubblicato });
          }}
        >
          {aperto.pubblicato ? 'Togli dalla circolazione' : 'Pubblica agli installatori'}
        </Button>
      </Stack>

      {senzaSegni && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nessuna parte è ancora segnata. Un apparecchio pubblicato così si riconosce e
          non indica niente — in cantiere sembra un guasto. Scegli una parte a sinistra e
          tocca la fotografia dove si trova.
        </Alert>
      )}

      {/*
        ⚠️ **La pagina non scorre: riempie la finestra.** È uno strumento, non un
        documento. Con un tavolo alto in modo fisso e l'assistente che cresce sotto, su
        un portatile la fotografia usciva dallo schermo proprio mentre si premeva un
        comando che agisce su quella fotografia — e non c'era modo di vedere l'effetto
        di ciò che si stava facendo. Qui l'altezza si divide: quello che avanza va alla
        tavola, e ogni colonna scorre per conto suo.
      */}
      <Box
        sx={{
          display: 'grid',
          /*
            ⚠️ **Tre colonne, non un cassetto sotto la tavola.** L'assistente sotto
            competeva in altezza con la fotografia: aprirlo la schiacciava, e il suo
            contenuto — documenti più proposta — è a sua volta lungo, quindi si finiva
            con uno scorrimento dentro un altro. In una colonna sua non ruba niente alla
            tavola e scorre per conto proprio. È il motivo per cui gli strumenti di
            annotazione mettono l'ispettore di fianco e non sotto.
          */
          gridTemplateColumns: { xs: '1fr', md: '288px 1fr', lg: '288px 1fr 340px' },
          gap: 2,
          alignItems: 'stretch',
          height: { md: 'calc(100vh - 188px)' },
          minHeight: { md: 460 },
        }}
      >
        <ElencoParti
          apparecchio={aperto}
          segniQui={superficie?.segni ?? []}
          parteScelta={parteScelta}
          onScegli={(id) => {
            // scegliere una parte la mostra; per posarla si arma esplicitamente
            setParteScelta(id);
            setArmato(false);
          }}
          armato={armato}
          onArma={(id) => {
            setParteScelta(id);
            setArmato(true);
          }}
          onDisarma={() => setArmato(false)}
          onTogli={togli}
          onNuova={() => {
            setParteInModifica(null);
            setSchedaAperta(true);
          }}
          onCorreggi={(p) => {
            setParteInModifica(p);
            setSchedaAperta(true);
          }}
          onElimina={togliParte}
        />

        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Tabs
            value={superficieId}
            onChange={(_, v) => {
              setSuperficieId(v);
              // ⚠️ cambiando faccia si disarma: restare armati su un'altra fotografia
              // significa posare la parte sbagliata al primo clic
              setArmato(false);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1, minHeight: 38, flexShrink: 0 }}
          >
            {aperto.superfici.map((s) => {
              const c = conteggio(s);
              return (
                <Tab
                  key={s.id}
                  value={s.id}
                  sx={{ minHeight: 38, textTransform: 'none' }}
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{s.nome}</span>
                      <Chip
                        size="small"
                        label={c.bozze > 0 ? `${c.posati}+${c.bozze}` : c.posati}
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    </Stack>
                  }
                />
              );
            })}
          </Tabs>

          {superficie ? (
            /*
              ⚠️ **Un contenitore solo, non blocchi impilati.** Tavola, legenda,
              documenti e assistente erano quattro elementi accostati sulla pagina, e
              scorrendo si perdeva quale apparteneva a cosa: la fotografia usciva dallo
              schermo mentre si leggeva un comando che opera su quella fotografia. Dentro
              un bordo unico si legge come un solo strumento, che è quello che è.
            */
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* ⚠️ `minHeight: 0` sul figlio elastico: senza, un contenitore flex non
                  scende mai sotto il suo contenuto e la tavola spinge fuori l'assistente
                  invece di cedergli spazio. */}
              <Box sx={{ flex: 1, minHeight: { xs: 300, md: 0 } }}>
                <TavoloDiMappatura
                  superficie={
                    anteprima
                      ? {
                          ...superficie,
                          segni: [
                            ...superficie.segni,
                            /*
                              ⚠️ **L'anteprima si rifiltra a ogni disegno, non solo quando
                              arriva.** Era filtrata una volta sola, al ritorno della
                              proposta: poi bastava segnare a mano una di quelle parti
                              perché la bozza vecchia continuasse a essere disegnata
                              accanto al punto nuovo — due cerchi con la stessa etichetta,
                              uno vero e uno fantasma, e nessuno dei due dichiarato tale.
                            */
                            ...anteprima
                              .filter((g) => !superficie.segni.some((x) => x.parte === g.parte))
                              .map((g) => ({
                                ...g,
                                r: Math.round(superficie.larghezza_pixel * RAGGIO_PREDEFINITO),
                              })),
                          ],
                        }
                      : superficie
                  }
                  parti={aperto.parti}
                  parteScelta={parteScelta}
                  armato={armato}
                  onPosata={() => setArmato(false)}
                  onPoni={poni}
                  onSposta={sposta}
                  onScegli={setParteScelta}
                  onRaggio={raggio}
                />
              </Box>
              <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'action.hover' }}>
                <Legenda />
              </Box>

              {/*
                ⚠️ L'assistente sta DENTRO lo stesso contenitore della fotografia su cui
                lavora, e sotto una riga che lo separa: chi vede una proposta sbagliata
                deve poter aggiungere la tavola giusta senza cambiare schermata e senza
                dubitare a quale fotografia si riferisca il comando che sta premendo.
              */}
            </Box>
          ) : (
            <Alert severity="warning">
              Questo apparecchio non ha fotografie. Rimappalo da YumaWORK.
            </Alert>
          )}
        </Box>

        {/* terza colonna: l'assistente, con il suo bordo e il suo scorrimento */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            flexDirection: 'column',
            minHeight: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          >
            <AutoAwesome sx={{ fontSize: 17, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Assistente</Typography>
          </Stack>
          {/* ⚠️ scorre qui dentro: una proposta lunga non deve spostare la fotografia */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.25, pb: 1.5 }}>
            {superficie && (
              <>
                <Riferimenti
                  apparecchioId={aperto.id}
                  riferimenti={aperto.riferimenti ?? []}
                  onCambio={(r) => setAperto({ ...aperto, riferimenti: r })}
                />
                <ConfrontoModelli
                  apparecchioId={aperto.id}
                  superficieId={superficie.id}
                  parti={aperto.parti}
                  giaSegnate={superficie.segni.map((g) => g.parte)}
                  onAnteprima={setAnteprima}
                  onAccetta={(bozze) =>
                    void scriviSegni([
                      ...superficie.segni,
                      ...bozze.map((g) => ({
                        ...g,
                        r: Math.round(superficie.larghezza_pixel * RAGGIO_PREDEFINITO),
                      })),
                    ])
                  }
                />
              </>
            )}
          </Box>
        </Box>
      </Box>

      <SchedaParte
        aperta={schedaAperta}
        parte={parteInModifica}
        esistenti={aperto.parti}
        onChiudi={() => setSchedaAperta(false)}
        onSalva={(p) => void salvaParte(p)}
      />
    </Box>
  );
}

function Intestazione() {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Apparecchi in realtà aumentata
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
        Le fotografie si fanno in cantiere con YumaWORK, dove sta l'apparecchio. Qui si
        segnano le parti al pixel giusto e si decide cosa mandare agli installatori.
      </Typography>
    </Box>
  );
}

function VuotoConDirezione() {
  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
      }}
    >
      <PhoneIphone sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
        Nessun apparecchio è ancora arrivato dal campo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: 'auto' }}>
        Da YumaWORK: Strumenti → realtà aumentata → mappa un apparecchio. Appena salvato,
        compare qui con le sue fotografie.
      </Typography>
    </Box>
  );
}

function VoceApparecchio({ a, onApri }: { a: ApparecchioAr; onApri: () => void }) {
  const segni = a.superfici.reduce((n, s) => n + s.segni.length, 0);
  return (
    <Box
      onClick={onApri}
      sx={{
        p: 1.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'border-color .15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600 }} noWrap>
            {a.marca} {a.modello}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {a.superfici.length} fotografie · {a.parti.length} parti ·{' '}
            {segni === 0 ? 'nessuna segnata' : `${segni} segnate`}
            {a.larghezza_mm > 0 &&
              ` · ${a.larghezza_mm}×${a.altezza_mm}×${a.profondita_mm} mm`}
          </Typography>
        </Box>
        {a.origine === 'telefono' && (
          <Chip size="small" icon={<PhoneIphone />} label="dal campo" variant="outlined" />
        )}
        <Chip
          size="small"
          label={a.pubblicato ? 'in circolazione' : 'bozza'}
          color={a.pubblicato ? 'success' : 'default'}
          variant={a.pubblicato ? 'filled' : 'outlined'}
        />
      </Stack>
    </Box>
  );
}

function ElencoParti({
  apparecchio,
  segniQui,
  parteScelta,
  onScegli,
  armato,
  onArma,
  onDisarma,
  onTogli,
  onNuova,
  onCorreggi,
  onElimina,
}: {
  apparecchio: ApparecchioAr;
  segniQui: SegnoAr[];
  parteScelta: string | null;
  onScegli: (id: string) => void;
  armato: boolean;
  onArma: (id: string) => void;
  onDisarma: () => void;
  onTogli: (id: string) => void;
  onNuova: () => void;
  onCorreggi: (p: ParteAr) => void;
  onElimina: (id: string) => void;
}) {
  const [conferma, setConferma] = useState<{ parte: ParteAr; aperta: boolean } | null>(null);
  const chiudiConferma = () => setConferma((c) => (c ? { ...c, aperta: false } : c));
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          px: 1,
          py: 0.5,
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'text.secondary',
          fontWeight: 600,
        }}
      >
        Le parti · segnate su questa faccia
      </Typography>
      <Divider sx={{ mb: 0.5 }} />
      {/* ⚠️ scorre da sola dentro la colonna: se scorresse la pagina, la fotografia
          uscirebbe dallo schermo per cercare una parte in fondo all'elenco */}
      <Stack spacing={0.25} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {apparecchio.parti.map((p) => {
          const qui = segniQui.find((g) => g.parte === p.id);
          const scelta = p.id === parteScelta;
          return (
            <Box
              key={p.id}
              onClick={() => onScegli(p.id)}
              sx={{
                px: 1,
                py: 0.75,
                borderRadius: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: scelta ? 'action.selected' : 'transparent',
                borderLeft: '3px solid',
                borderLeftColor: qui ? coloreDi(qui.origine) : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    // ⚠️ Monospaziato: le sigle sono codici stampati, si confrontano
                    // carattere per carattere, e in un proporzionale un 1 accanto a una l
                    // è un errore che aspetta di succedere.
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 12,
                    letterSpacing: '.03em',
                    fontWeight: 600,
                  }}
                  noWrap
                >
                  {p.sigla?.trim() || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {p.nome}
                </Typography>
              </Box>
              {/*
                ⚠️ Il comando che ARMA sta sulla riga, non nella fotografia: è lì che si
                decide quale parte si sta per posare, e leggerne il nome mentre si preme
                è ciò che impedisce di posare quella sbagliata.
              */}
              {scelta && (
                <Button
                  size="small"
                  variant={armato ? 'contained' : 'outlined'}
                  color={armato ? 'warning' : 'primary'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (armato) onDisarma();
                    else onArma(p.id);
                  }}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0.15,
                    fontSize: 11,
                    textTransform: 'none',
                    flexShrink: 0,
                  }}
                >
                  {armato ? 'annulla' : qui ? 'rimetti' : 'posa'}
                </Button>
              )}
              {qui ? (
                <>
                  <CheckCircleOutline sx={{ fontSize: 17, color: coloreDi(qui.origine) }} />
                  <DeleteOutline
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogli(p.id);
                    }}
                    titleAccess="Togli il punto da questa faccia"
                    sx={{
                      fontSize: 17,
                      color: 'text.disabled',
                      '&:hover': { color: 'error.main' },
                    }}
                  />
                </>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  da segnare
                </Typography>
              )}
              {/*
                ⚠️ Correggere una parte è un'altra cosa dal togliere il suo punto, e i
                due comandi non devono stare a un pixel di distanza: uno cambia una
                parola, l'altro cancella lavoro. La matita compare solo sulla riga
                scelta, dove chi la preme sa già su cosa sta lavorando.
              */}
              {scelta && (
                <EditOutlined
                  onClick={(e) => {
                    e.stopPropagation();
                    onCorreggi(p);
                  }}
                  titleAccess="Correggi nome, sigla o spiegazione"
                  sx={{ fontSize: 16, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                />
              )}
            </Box>
          );
        })}
      </Stack>
      <Divider sx={{ my: 0.75 }} />
      <Button
        size="small"
        fullWidth
        startIcon={<AddCircleOutline sx={{ fontSize: 17 }} />}
        onClick={onNuova}
        sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 1 }}
      >
        Aggiungi una parte
      </Button>
      {/*
        ⚠️ Il comando che ELIMINA una parte non sta in elenco accanto alle altre azioni:
        toglie anche tutti i suoi cerchi su ogni faccia. Sta dentro la scheda di
        correzione, dove chi ci arriva sta già guardando quella parte e non un elenco.
      */}
      {parteScelta && (
        <Button
          size="small"
          fullWidth
          color="error"
          startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
          onClick={() => {
            const p = apparecchio.parti.find((x) => x.id === parteScelta);
            if (!p) return;
            setConferma({ parte: p, aperta: true });
          }}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 1, mt: 0.25 }}
        >
          Togli «{apparecchio.parti.find((x) => x.id === parteScelta)?.nome ?? ''}»
        </Button>
      )}
      {/*
        La conferma è una finestra della pagina, non quella del browser: la nativa blocca
        la pagina finché resta aperta e non segue il tema. La parte resta in `conferma`
        anche mentre la finestra si chiude, così il titolo non si svuota durante l'uscita.
      */}
      <Dialog
        open={conferma?.aperta ?? false}
        onClose={chiudiConferma}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Togliere «{conferma?.parte.nome ?? ''}» dal catalogo?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Spariscono anche i suoi cerchi su tutte le facce.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button autoFocus onClick={chiudiConferma} sx={{ minHeight: 40 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const id = conferma?.parte.id;
              chiudiConferma();
              if (id) onElimina(id);
            }}
            sx={{ minHeight: 40 }}
          >
            Togli
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Legenda() {
  const voci = [
    { c: COLORI.posato, t: 'segnata da una persona' },
    { c: COLORI.serigrafia, t: 'letta dalla scritta sull’apparecchio' },
    { c: COLORI.bozza, t: 'proposta da un modello — da confermare' },
  ];
  return (
    <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
      {voci.map((v) => (
        <Stack key={v.t} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 11,
              height: 11,
              borderRadius: '50%',
              border: `2px solid ${v.c}`,
              ...(v.c === COLORI.bozza ? { borderStyle: 'dashed' } : {}),
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {v.t}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
